import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MixpanelService } from '@core/services/mixpanel.service';
import { SpinnerService } from '@core/services/spinner.service';
import { TimeZoneService } from '@core/services/time-zone.service';
import { TranslationService } from '@core/services/translation.service';
import { CyclesAPI } from '@core/typings/api/cycles.typing';
import { TimeZone } from '@core/typings/api/time-zone.typing';
import { MyApplicationForUi } from '@core/typings/application.typing';
import { ProgramDetail } from '@core/typings/program.typing';
import { ProgramTranslationMap } from '@core/typings/translation.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { FormTypes } from '@features/configure-forms/form.typing';
import { CopyApplicationModalComponent } from '@features/copy-application/copy-application-modal/copy-application-modal.component';
import { CopyApplicationService } from '@features/copy-application/copy-application.service';
import { CyclesService } from '@features/cycles/cycles.service';
import { InvitationService } from '@features/invitations/invitation.service';
import { InvitationApplicantHelpers, InvitationDetail } from '@features/invitations/invitation.typing';
import { ProgramAutomationService } from '@features/programs/program-automation/program-automation.service';
import { ProgramService } from '@features/programs/services/program.service';
import {
  TableRepository,
  TableRepositoryFactory
} from '@yourcause/common';
import { DATE_TIME_FORMAT, DateService } from '@yourcause/common/date';
import { HeapService, TrackingEventNames, TrackingPropertyNames } from '@yourcause/common/heap';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmationModalComponent, ModalFactory } from '@yourcause/common/modals';
import { ApplicationApplicantService } from '../application-applicant.service';

@Component({
  selector: 'gc-public-program-page',
  templateUrl: './public-program-page.component.html',
  styleUrls: ['./public-program-page.component.scss']
})
export class PublicProgramPageComponent implements OnInit, OnDestroy {
  programGuid: string;
  FormTypes = FormTypes;
  program: ProgramDetail;
  programValid: boolean;
  now = new Date();
  continueText = this.i18n.translate(
    'APPLY:textStartNewApplication',
    {},
    'Start new application'
  );
  loaded = false;
  repository: TableRepository<MyApplicationForUi>;
  isNomination = false;
  translationMap: ProgramTranslationMap;
  programTimeZone: TimeZone;
  programAcceptanceText: string;
  programDatesText: string;
  canApply: boolean;
  programIsArchived: boolean;
  programIsArchivedText = this.i18n.translate(
    'APPLY:textProgramIsArchivedCannotAcceptApplications',
    {},
    'This program is archived and cannot accept applications.'
  );
  programIsAcceptingText = this.i18n.translate(
    'APPLY:textProgramIsAcceptingApplications',
    {},
    'Program is accepting applications'
  );
  programIsAcceptingNominations = this.i18n.translate(
    'APPLY:textProgramIsAcceptingNominations',
    {},
    'Program is accepting nominations'
  );
  programIsNotAcceptingText = this.i18n.translate(
    'APPLY:textProgramIsNotAccepting',
    {},
    'Program is not accepting applications'
  );
  programIsNotAcceptingNominations = this.i18n.translate(
    'APPLY:textProgramIsNotAcceptingNominations',
    {},
    'Program is not accepting nominations'
  );
  invitationRequiredText: string;
  inviteHelpers: InvitationApplicantHelpers;
  programAcceptingNoDates: string;
  programNotAcceptingNoDates: string;
  alreadyAppliedText: string;
  invitationNotFoundText: string;
  onCopyStep = false;
  programName: string;
  currentCycle: CyclesAPI.BaseProgramCycle;

  constructor (
    private mixpanel: MixpanelService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private programService: ProgramService,
    private spinnerService: SpinnerService,
    private i18n: I18nService,
    private applicationApplicantService: ApplicationApplicantService,
    private copyApplicationService: CopyApplicationService,
    private autoTableFactory: TableRepositoryFactory,
    private translationService: TranslationService,
    private timeZoneService: TimeZoneService,
    private cyclesService: CyclesService,
    private modalFactory: ModalFactory,
    private invitationService: InvitationService,
    private clientSettingsService: ClientSettingsService,
    private programAutomationService: ProgramAutomationService,
    private dateService: DateService,
    private heapService: HeapService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  get hasDraftApplication () {
    if (this.repository && this.repository.currentSet) {
      return this.repository.currentSet.length > 0;
    }

    return false;
  }

  async ngOnInit () {
    const inviteIdString = this.activatedRoute.snapshot.queryParamMap.get('invite');
    const inviteId = inviteIdString ? +inviteIdString : null;
    this.programGuid = this.activatedRoute.snapshot.params.programId;
    this.spinnerService.startSpinner();
    const program = await this.programService.handleGetProgramForApplicant(this.programGuid, true);
    if (!program) {
      // If we can't fetch the program, we can't proceed
      this.spinnerService.stopSpinner();
      await this.router.navigate(['/apply/applications']);

      return;
    }
    this.program = program;

    const [
      inviteHelpers,
      invitation
    ] = await Promise.all([
      this.programService.canApplicantApplyToProgram(
        this.programGuid
      ),
      this.invitationService.getInvitationDetail(this.program.grantProgramId, inviteId)
    ]);

    this.programIsArchived = inviteHelpers.programIsArchived;

    await this.setAffiliateForMixpanel();
    await this.setClientBranding();
    this.programTimeZone = this.timeZoneService.returnTimeZoneFromID(
      this.program.timezoneId
    );
    this.inviteHelpers = inviteHelpers;
    this.translationMap = this.translationService.getProgramTranslationMapForApplicant(
      this.program
    );
    this.programName = this.translationMap.name;

    this.setNominationAttrs();
    this.setRepository();
    this.checkProgramCycles(this.program.cycles);
    this.programValid = this.program.active && this.canApply;
    this.handleInvitation(invitation);
    this.loaded = true;
    
    // track event
    this.heapService.track(
      TrackingEventNames.PageView,
      {
        [TrackingPropertyNames.ClientId]: this.program.clientId,
        [TrackingPropertyNames.ClientName]: this.program.clientName,
        [TrackingPropertyNames.ProgramId]: this.program.grantProgramId,
        [TrackingPropertyNames.ProgramName]: this.programName
      }
    );
    this.spinnerService.stopSpinner();
  }

  async setAffiliateForMixpanel () {
    await this.clientSettingsService.setAffiliateInfo();
    const affiliateInfo = this.clientSettingsService.getClientAffiliateInfo(this.program.clientName);
    this.mixpanel.register({
      ...affiliateInfo,
      [TrackingPropertyNames.ClientName]: this.program.clientName,
      [TrackingPropertyNames.ProgramName]: this.program.grantProgramName
    });
  }

  async setClientBranding () {
    await this.applicationApplicantService.getClientBranding(this.program.clientId);
  }

  handleInvitation (invitation: InvitationDetail) {
    if (invitation?.invitationUsed) {
      const foundApp = this.applicationApplicantService.myApplications.find((app) => {
        return !!invitation.organizationId ?
          app.invitationId === invitation.invitationId && app.organizationId === invitation.organizationId : 
          app.invitationId === invitation.invitationId;
      });
      if (!!foundApp && this.program.inviteOnly) {
        // If we find app they created and this program requires an invite,
        // Send them to the invitation app
        // If the program does not require an invite this will be skipped over and they can apply again
        const defaultFormId = this.program.form?.formId;
        const requestForm = foundApp.applicationForms.find((form) => {
          return form.formId === defaultFormId;
        });
        
        this.router.navigate([
          `/apply/application/${foundApp.applicationId}/forms/${requestForm.formId}`
        ]);
      } else if (this.program.inviteOnly) {
        // In this case, the invitation was accepted by another applicant on the invitation (NPOc use case)
        // and cannot be re-used
        this.alreadyAppliedText = this.i18n.translate(
          'common:textInvitationAlreadyAccepted',
          {},
          'The invitation was already accepted and cannot be used again.'
        );
        this.canApply = false;
        this.programValid = this.program.active && this.canApply;
      }
    } else if (
      this.programValid &&
      !!invitation?.invitationId &&
      !!invitation?.organizationId
    ) {
      // If coming from invitation where org is defined,
      // we navigate to Apply page where app will be created with the selected org
      this.navigateToApplyNew(invitation.organizationId);
    } else if (
        (!invitation || !invitation.invitationId) && 
        this.program.inviteOnly
      ) {
      // If invitation was not found, and program is invite only
      // we show the invitation not found text
      this.invitationNotFoundText = this.i18n.translate(
        'common:textInvitationNotFound',
        {},
        'The invitation could not be found. Please check the URL and try again.'
      );
      this.canApply = false;
      this.programValid = this.program.active && this.canApply;
    }
  }

  setNominationAttrs () {
    this.isNomination = this.program.form.formType === FormTypes.NOMINATION;
    this.programAcceptingNoDates = this.isNomination ?
      this.programIsAcceptingNominations :
      this.programIsAcceptingText;
    this.programNotAcceptingNoDates = this.isNomination ?
      this.programIsNotAcceptingNominations :
      this.programIsNotAcceptingText;
    if (this.isNomination) {
      this.continueText = this.i18n.translate(
        'APPLY:textStartNewNomination',
        {},
        'Start new nomination'
      );
    }
  }

  checkProgramCycles (cycles: CyclesAPI.BaseProgramCycle[]) {
    const cyclesForDisplay = this.cyclesService.getCycleDateHelpers(cycles);
    this.currentCycle = cyclesForDisplay.currentCycle;
    this.setTextForAcceptingApplications(cyclesForDisplay);
  }

  setTextForAcceptingApplications (payload: CyclesAPI.CycleHelpers) {
    this.canApply = !!this.currentCycle &&  this.inviteHelpers.canApply;
    if (this.inviteHelpers.alreadyUsedInvite && !this.inviteHelpers.canApply) {
      this.alreadyAppliedText = this.i18n.translate(
        'APPLY:textYouHaveMetMaxiumumNumberOfAppsForProgram',
        {},
        'You have met the maximum number of applications for this program'
      );
    } else if (
      !this.inviteHelpers.canApply &&
      !this.inviteHelpers.alreadyUsedInvite &&
      this.program.inviteOnly
    ) {
      if (!this.inviteHelpers.grantProgramCycleIdOfNextInvitation) {
        this.invitationRequiredText = this.i18n.translate(
          'APPLY:textItLooksLikeYouWereNotInvited',
          {},
          'It looks like you were not invited to apply for this program.'
        );
      }
    }
    if (this.program.hideCycleDatesInApplicantPortal) {
      this.programAcceptanceText = this.canApply ?
          this.programAcceptingNoDates :
          this.programNotAcceptingNoDates;
    } else {
      if (this.canApply) {
        this.programAcceptanceText = this.i18n.translate(
          this.isNomination ?
            'APPLY:textAcceptingNominationsUntilDate' :
            'APPLY:textAcceptingApplicationsUntilDate',
          {
            date: this.getDateForDisplay(this.currentCycle.endDate)
          },
          this.isNomination ?
            'Accepting nominations until __date__' :
            'Accepting applications until __date__'
        );
      } else {
        this.programAcceptanceText = this.isNomination ?
          this.programIsNotAcceptingNominations :
          this.programIsNotAcceptingText;
        const nextCycleId = payload.nextCycle && +payload.nextCycle.id;
        const lastCycleId = payload.lastCycle && +payload.lastCycle.id;
        const canApplyNextCycle = this.canApplyToCycle(nextCycleId);
        const canApplyLastCycle = this.canApplyToCycle(lastCycleId);
        if (payload.nextCycle && canApplyNextCycle) {
          this.programDatesText = this.i18n.translate(
            this.isNomination ?
              'APPLY:textAcceptingNominationsInFuture' :
              'APPLY:textAcceptingApplicationsInFuture',
            {
              startDate: this.getDateForDisplay(payload.nextCycle.startDate),
              endDate: this.getDateForDisplay(payload.nextCycle.endDate)
            },
            this.isNomination ?
              'Accepting nominations from __startDate__ until __endDate__' :
              'Accepting applications from __startDate__ until __endDate__'
          );
        } else if (payload.lastCycle && canApplyLastCycle) {
          this.programDatesText = this.i18n.translate(
            'APPLY:textProgramClosedOnDate',
            {
              endDate: this.getDateForDisplay(payload.lastCycle.endDate)
            },
            'Program closed on __endDate__'
          );
        }
      }
    }
  }

  canApplyToCycle (cycleId: number) {
    if (this.program.inviteOnly) {
      return !this.inviteHelpers.alreadyUsedInvite &&
        (!this.inviteHelpers.grantProgramCycleIdOfNextInvitation || (+this.inviteHelpers.grantProgramCycleIdOfNextInvitation === cycleId));
    }

    return true;
  }

  getDateForDisplay (date: string) {
    return this.dateService.formatDate(date, DATE_TIME_FORMAT);
  }

  setRepository () {
    this.repository = this.autoTableFactory.create({
      key: 'PROGRAM_APPLICATIONS',
      columns: [],
      rowsPerPage: 1000,
      rows: this.applicationApplicantService.getDraftAppsForProgram(
        this.programGuid,
        this.program.cycles
      ),
      changeDetectorRef: this.changeDetectorRef
    });
  }

  onContinueToApp () {
    const hasApps = this.applicationApplicantService.checkForExistingSubmittedApps(
      this.program.grantProgramId
    );
    if (hasApps && this.program.allowApplicationCopyForApplicant) {
      this.handleCopyStep();
    } else {
      this.navigateToApplyNew();
    }
  }

  selectDraftApp (application: MyApplicationForUi) {
    const draftForms = application.applicationForms
      .filter((form) => !!form.isDraft)
      .map((form) => form.formId);
    this.router.navigateByUrl(
      `/apply/application/${application.applicationId}/forms/${draftForms[0]}`
    );
  }

  navigateToApplyNew (organizationId?: number) {
    let queryParams;
    this.heapService.track(
      TrackingEventNames.NewApplication,
      {
        [TrackingPropertyNames.ClientId]: this.program.clientId,
        [TrackingPropertyNames.ClientName]: this.program.clientName,
        [TrackingPropertyNames.ProgramId]: this.program.grantProgramId,
        [TrackingPropertyNames.ProgramName]: this.programName
      }
    )
    const routingApp = this.programAutomationService.routingAppForSave;
    organizationId = organizationId ?? routingApp?.orgId;
    if (organizationId) {
      queryParams = {
        organizationId
      };
    }
    this.router.navigate(
      [`/apply/programs/${this.programGuid}/apply`],
      {
        queryParams
      }
    );
  }

  navigateToCopyOf (copyOf: number) {
    this.router.navigate([
      `/apply/programs/${this.programGuid}/apply`
    ], {
      queryParams: {
        copyOf
      }
    });
  }

  async viewGuidelines () {
    const guidelinesTranslation = this.translationMap.guidelines ??
      this.program.grantProgramGuidelines;
    await this.modalFactory.open(
      ConfirmationModalComponent,
      {
        confirmText: guidelinesTranslation,
        useRichTextDisplay: true,
        modalHeader: this.i18n.translate(
          'PROGRAM:textProgramGuidelinesTitle',
          {},
          'Program Guidelines'
        )
      }
    );
  }

  async handleCopyStep () {
    this.onCopyStep = true;
    const response = await this.modalFactory.open(
      CopyApplicationModalComponent,
      {
        isNomination: this.isNomination,
        programId: this.program.grantProgramId,
        programName: this.programName
      }
    );
    if (response) {
      if (response.goBack) {
        this.onCopyStep = false;
      } else if (response.startNew) {
        this.navigateToApplyNew();
      } else {
        const copyResponse = await this.copyApplicationService.handleCopyApplication(
          response.appToCopy.applicationId,
          response.appToCopy.grantProgramId,
          this.currentCycle?.id || response.appToCopy.cycleId,
          response.appToCopy.organizationId,
          this.isNomination,
          true,
          this.program.clientId
        );
        if (!copyResponse?.passed) {
          this.router.navigate(['/apply/applications']);
        }
      }
    }
  }

  ngOnDestroy () {
    this.mixpanel.unregister(TrackingPropertyNames.ProgramName);
    this.mixpanel.unregister(TrackingPropertyNames.ClientName);
    this.applicationApplicantService.clearClientBranding();
  }
}
